import type { PropsWithChildren } from "react";
import { useCallback } from "react";
import React from "react";
import type { CKFilterContextProps, CKFilterOnSelectParams } from "./CKFilterContext";
import { CKFilterContext } from "./CKFilterContext";
import { CKFilterReset } from "./CKFilterReset";
import { CKFilterSelected } from "./CKFilterSelected";
import { CKFilterItem } from "./CKFilterItem";
import { CKFilterGroup } from "./CKFilterGroup";
import { useControllable } from "@commercekit/react";

export interface CKFilterProps<T = any> extends PropsWithChildren {
  value?: T[];
  onChange?(v: T[]): void;
  defaultValue?: T[];
  /** if provided we will run this to compare, without this we just use == */
  identity?(item: T): React.Key | T;
}

function CKFilterRoot<T = any>(props: CKFilterProps<T>) {
  const { value: controlledValue, onChange: controlledChange, defaultValue, identity = (v) => v, children } = props;

  const [value, onChange] = useControllable(controlledValue, controlledChange, defaultValue);

  const onReset = useCallback(() => {
    onChange([]);
  }, [onChange]);

  const removeItems = useCallback(
    (origin: T[]) => {
      const removeKeys = origin.map((o) => identity(o));
      return value.filter((v) => !removeKeys.includes(identity(v)));
    },
    [identity, value]
  );

  const onUnCheck: CKFilterContextProps<T>["onUnCheck"] = useCallback(
    (origin) => {
      onChange(removeItems(origin));
    },
    [onChange, removeItems]
  );

  const onUpdate = useCallback(
    (info: CKFilterOnSelectParams<T>) => {
      const originRemovedArr = removeItems(info.origin);

      switch (info.type) {
        case "checkbox":
          return onChange(info.checked ? [...value, ...info.origin] : originRemovedArr);
        case "range":
          return onChange([
            ...originRemovedArr,
            ...(info.checked
              ? info.origin.map((o, idx) => ({
                  ...o,
                  ...info.current?.[idx],
                }))
              : []),
          ]);
        default:
      }
    },
    [onChange, removeItems, value]
  );

  const ifChecked = useCallback(
    (currentItem: T) => {
      return value.some((item) => identity(item) === identity(currentItem));
    },
    [value, identity]
  );

  return (
    <CKFilterContext.Provider
      value={{
        value,
        onChange,
        onUpdate,
        onUnCheck,
        onReset,
        ifChecked,
      }}
    >
      {children}
    </CKFilterContext.Provider>
  );
}

export const CKFilter = Object.assign(CKFilterRoot, {
  Reset: CKFilterReset,
  Selected: CKFilterSelected,
  Group: CKFilterGroup,
  Item: CKFilterItem,
});
